import { useState, useMemo, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Download,
  Filter,
  Wallet,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { mt5Api, MT5Transaction } from '../../services/mt5ApiService';

const IBWallet = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [transactions, setTransactions] = useState<MT5Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await mt5Api.getTransactions();
        setTransactions(response.data.transactions);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const filterFields: FilterField[] = [
    {
      id: 'type',
      label: 'Transaction Type',
      options: [
        { value: 'deposit', label: 'Deposit' },
        { value: 'withdrawal', label: 'Withdrawal' },
        { value: 'transfer', label: 'Transfer' },
        { value: 'credit', label: 'Credit' },
        { value: 'credit_remove', label: 'Credit Remove' },
      ],
    },
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
      ],
    },
  ];

  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
      if (activeFilters.type && transaction.type !== activeFilters.type) {
        return false;
      }
      if (activeFilters.status && transaction.status !== activeFilters.status) {
        return false;
      }
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          transaction.comment.toLowerCase().includes(searchLower) ||
          transaction.transaction_id.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [transactions, activeFilters, searchValue]);

  const columns: DataTableColumn<MT5Transaction>[] = [
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge className={getStatusColor(row.type)}>{row.type}</Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '120px',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => (
        <span
          className={`font-semibold ${
            row.type === 'deposit' || row.type === 'credit'
              ? 'text-green-600'
              : 'text-red-600'
          }`}
        >
          {row.type === 'withdrawal' || row.type === 'credit_remove'
            ? '-'
            : '+'}
          ${parseFloat(row.amount).toFixed(2)}
        </span>
      ),
      sortable: true,
      sortValue: row => parseFloat(row.amount),
      width: '130px',
    },
    {
      id: 'comment',
      header: 'Comment',
      accessor: row => row.comment,
      sortable: true,
      sortValue: row => row.comment,
      width: '120px',
    },
    {
      id: 'transaction_id',
      header: 'Transaction ID',
      accessor: row => (
        <code className="px-2 py-1 bg-muted rounded text-sm">
          {row.transaction_id}
        </code>
      ),
      sortable: true,
      sortValue: row => row.transaction_id,
      width: '150px',
    },
    {
      id: 'createdAt',
      header: 'Date',
      accessor: row => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.createdAt,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={String(row.id)}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={false}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View transaction:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB Wallet"
          description="Manage wallet balance and transactions"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Available Balance"
          value="$0.00"
          change="+0% from last month"
          icon={Wallet}
        />
        <StatsCard
          title="Total Earned"
          value="$0.00"
          change="+0% from last month"
          icon={DollarSign}
        />
        <StatsCard
          title="Total Withdrawn"
          value="$0.00"
          change="+0% from last month"
          icon={ArrowDownRight}
        />
        <StatsCard
          title="Pending"
          value="$0.00"
          change="+0% from last month"
          icon={ArrowUpRight}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>
                View and manage all wallet transactions
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-gray-500">Loading transactions...</p>
            </div>
          ) : (
            <>
              <InlineFilter
                showFilters={showFilters}
                onToggle={() => setShowFilters(!showFilters)}
                filterFields={filterFields}
                activeFilters={activeFilters}
                onFilterChange={setActiveFilters}
                onFiltersClear={() => setActiveFilters({})}
                onFiltersApply={() => {}}
                title="Filter Transactions"
              />
              <DataTable
                columns={columns}
                data={filteredTransactions}
                searchValue={searchValue}
                searchPlaceholder="Search transactions..."
                onSearchChange={setSearchValue}
                onSearchReset={() => setSearchValue('')}
                filterFields={filterFields}
                activeFilters={activeFilters}
                onFiltersChange={() => {}}
                showColumnVisibility={true}
                showAddButton={false}
                showExportButton={false}
                initialPageSize={10}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IBWallet;
